import React, { Component } from 'react';
import { RuleApplication, Rule, DefineRule } from '../Rule';
import { LinkedList, Vector } from 'prelude-ts';
import styles from '../styles/ProofTree.module.css';
import Statement from '../Statement';
//@ts-ignore
import MathJax from 'react-mathjax';

interface Props {
  rules: LinkedList<RuleApplication>;
  printDepth: number;
}

interface Derivation {
  premises: Vector<Derivation> | undefined;
  rule: Rule | undefined;
  conclusion: Statement;
}

function concat(comb: string): (s1: string, s2: string) => string {
  return (s1: string, s2: string) => {
    return s2 !== '' ? s1 + comb + s2 : s1;
  };
}

function printStatement(statement: Statement, isLeaf: boolean): string {
  const resourceString = statement.resources
    .map(f => f.toTex())
    .foldRight('', concat(','));

  const requirementString = statement.requirements
    .map(f => f.toTex())
    .foldRight('', concat(','));
  if (resourceString === '' && requirementString === '') {
    return isLeaf ? '' : ' \\dotsc ';
  }
  return resourceString + ' \\vdash ' + requirementString;
}

function toDerivationTree(list: LinkedList<RuleApplication>): Derivation {
  list = list.reverse();
  const listHead = list.head().getOrThrow();
  const root: Derivation = {
    premises: undefined,
    rule: undefined,
    conclusion: listHead.result.get(0).getOrThrow()
  };
  const frontier = { [root.conclusion.id]: root };
  list = list.tail().getOrThrow();
  list.forEach(ra => {
    const derivation = frontier[ra.to.id];
    derivation.premises = ra.appResult.map(stmt => {
      const newDerivation = {
        premises: undefined,
        rule: undefined,
        conclusion: stmt
      };
      frontier[stmt.id] = newDerivation;
      return newDerivation;
    });
    derivation.rule = ra.rule;
  });
  return root;
}

function truncate(node: Derivation, depth: number): Derivation {
  if (node.premises === undefined || node.premises.isEmpty()) {
    return node;
  } else {
    const maxDepth = getDepth(node, true);
    if (maxDepth <= depth) {
      return node;
    } else {
      const minDepth = getDepth(node, false);
      const result: Derivation = {
        premises: node.premises.map(p => truncate(p, depth - 1)),
        rule: node.rule,
        conclusion: node.conclusion
      };
      if (minDepth >= depth) {
        result.conclusion = new Statement(Vector.empty(), Vector.empty());
        result.premises = node.premises.map(p => truncate(p, depth));
      }
      return result;
    }
  }
}

function getDepth(node: Derivation, isMax: boolean): number {
  if (node.premises === undefined || node.premises.isEmpty()) {
    return 1;
  } else {
    const maxDepth = node.premises
      .map(d => getDepth(d, isMax))
      .reduce((d1: number, d2: number) => {
        return d1 > d2 && isMax ? d1 : d2;
      });
    return maxDepth.getOrThrow() + 1;
  }
}

function toTex(node: Derivation): string {
  if (node.premises === undefined) {
    return printStatement(node.conclusion, true);
  }
  const premiseText = node.premises
    .map(n => toTex(n))
    .foldRight('', concat(' \\quad '));
  const ruleName = node.rule === undefined ? '' : node.rule.name();
  return (
    '\\frac{' +
    premiseText +
    '}{' +
    printStatement(node.conclusion, false) +
    '}' +
    ruleName
  );
}
class ProofTree extends Component<Props> {
  static defaultProps = {
    printDepth: 2
  } as Props;

  render() {
    const derivTree = !this.props.rules.isEmpty()
      ? toDerivationTree(this.props.rules)
      : {
          premises: undefined,
          rule: new DefineRule(),
          conclusion: new Statement(Vector.empty(), Vector.empty())
        };
    const treeDepth = getDepth(derivTree, true);
    const skipTree = truncate(derivTree, this.props.printDepth);
    const proofString =
      this.props.printDepth > 0 && treeDepth > this.props.printDepth
        ? toTex(skipTree)
        : toTex(derivTree);

    return (
      <div className={styles.proofTree}>
        <MathJax.Provider>
          <MathJax.Node formula={proofString} />
        </MathJax.Provider>
      </div>
    );
  }
}

export default ProofTree;
